import { MoreIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash';
import { useCallback } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { Device } from 'dashboard/models/device';
import { ProductFeatureName } from 'dashboard/models/organization';
import AccountRemoveDeviceModal from 'dashboard/views/DashboardAccountsShowView/components/AccountRemoveDeviceModal';
import { IconButton } from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';

interface AccountDeviceDetailProps {
  device: Device;
  isRedacted?: boolean;
  handleRemoveDevice: (deviceId: string) => void;
}

const AccountDeviceDetail = ({
  device,
  isRedacted,
  handleRemoveDevice,
}: AccountDeviceDetailProps) => {
  const {
    'browser-full-version': browserFullVersion,
    'browser-name': browserName,
    'device-type': deviceType,
    'device-name': deviceName,
    'os-full-version': osFullVersion,
    'os-name': osName,
    'device-fingerprint': deviceFingerprint,
    'device-fingerprint-components': deviceFingerprintComponents,
  } = device?.attributes || {};

  const firstSeenAt = deviceFingerprintComponents?.['first-seen-at']?.global || '';
  const lastSeenAt = deviceFingerprintComponents?.['last-seen-at']?.global || '';

  const allowDeviceFingerprint = useProductFeature(
    ProductFeatureName.InquiriesEnableDeviceFingerprint,
  );

  const [removeDeviceModal, { enable: showRemoveDeviceModal, disable: hideRemoveDeviceModal }] =
    useToggle(false);

  const handleRemoveDeviceAndClose = useCallback(async () => {
    handleRemoveDevice(device.id);
    hideRemoveDeviceModal();
  }, [device.id, handleRemoveDevice, hideRemoveDeviceModal]);

  return (
    <>
      <Card>
        <Card.Body gap={0}>
          <TitleContainer>
            <TitleContents>
              <Heading size='sm' color='primary'>
                Device: {device.id}
              </Heading>
            </TitleContents>

            <div>
              <DropdownMenu
                trigger={
                  <IconButton data-test='account-device__more'>
                    <MoreIcon />
                  </IconButton>
                }
              >
                <DropdownMenu.Item
                  variant='danger'
                  id='account-device__untrust'
                  onClick={showRemoveDeviceModal}
                >
                  Detach device
                </DropdownMenu.Item>
              </DropdownMenu>
            </div>
          </TitleContainer>
          <Spacer height={3} />
          <AttributeGrid>
            <AttributeItem
              key='Device Type'
              title='Device Type'
              value={capitalize([deviceType, deviceName].filter(Boolean).join(', '))}
              isRedacted={isRedacted}
              placeholder='Unknown'
            />
            <AttributeItem
              key='Device OS'
              title='Device OS'
              value={[osName, osFullVersion].filter(Boolean).join(' ')}
              isRedacted={isRedacted}
              placeholder='Unknown'
            />
            <AttributeItem
              key='Browser'
              title='Browser'
              value={[browserName, browserFullVersion].filter(Boolean).join(' ')}
              isRedacted={isRedacted}
              placeholder='Unknown'
            />
            {allowDeviceFingerprint && (
              <>
                <AttributeItem
                  key='Device Fingerprint'
                  title={
                    <>
                      Device Fingerprint
                      <HelpIcon popoverWidth={300}>
                        Unique value computed from a systematic collection of information about a
                        device
                      </HelpIcon>{' '}
                    </>
                  }
                  value={deviceFingerprint}
                  isRedacted={isRedacted}
                  placeholder='Unknown'
                />
                <AttributeItem
                  key='First Seen'
                  title='First Seen'
                  value={formatDate(firstSeenAt)}
                  isRedacted={isRedacted}
                  placeholder='Unknown'
                />
                <AttributeItem
                  key='Last Seen'
                  title='Last Seen'
                  value={formatDate(lastSeenAt)}
                  isRedacted={isRedacted}
                  placeholder='Unknown'
                />
              </>
            )}
          </AttributeGrid>
          <Spacer height={1} />
        </Card.Body>
      </Card>
      <AccountRemoveDeviceModal
        removeDeviceModal={removeDeviceModal}
        handleRemoveDeviceAndClose={handleRemoveDeviceAndClose}
        hideRemoveDeviceModal={hideRemoveDeviceModal}
      />
    </>
  );
};

export default AccountDeviceDetail;

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TitleContents = styled('div')`
  display: flex;
  align-items: center;
`;
